// Quick-action sheets on the phone customer file: Call (dial + log), Note, Promise to pay. Same endpoints as the desktop quick actions.
import { useMemo, useState } from "react";
import { Loader2, Phone, PhoneCall, StickyNote, Target } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BottomSheet, SectionTitle } from "@/components/phone/PhoneBits";
import { addAgeingNote, createAgeingP2P } from "@/lib/api";
import { cur } from "@/lib/regionFmt";
import { cn } from "@/lib/utils";

const err = (e) => e?.response?.data?.detail || e?.message || "Something went wrong";
const localIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const plusDays = (n) => { const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + n); return localIso(d); };

export function phoneNumbersOf(caseRow, contacts) {
  const out = [];
  const seen = new Set();
  const push = (label, phone) => { const p = String(phone || "").trim(); if (!p || seen.has(p)) return; seen.add(p); out.push({ label, phone: p }); };
  push(caseRow.borrower_director_name || caseRow.customer_name || "Borrower", caseRow.borrower_phone || caseRow.primary_phone || caseRow.borrower_mobile);
  (contacts || []).forEach((c) => push(`${c.name || "Contact"}${c.role ? ` · ${c.role}` : ""}`, c.phone));
  (caseRow.additional_contacts || []).forEach((c) => push(`${c.name || "Contact"}${c.role ? ` · ${c.role}` : ""}`, c.phone));
  return out;
}

const OUTCOMES = ["Spoke to customer", "No answer", "Voicemail left", "Call back arranged", "Wrong number"];

export function CallSheet({ open, onClose, caseRow, contacts, preset, onDone }) {
  const numbers = useMemo(() => phoneNumbersOf(caseRow, contacts), [caseRow, contacts]);
  const [to, setTo] = useState(preset || numbers[0]?.phone || "");
  const [outcome, setOutcome] = useState("");
  const [text, setText] = useState("");
  const [promise, setPromise] = useState(false);
  const [amount, setAmount] = useState(caseRow.arrears_amount ? String(caseRow.arrears_amount) : "");
  const [date, setDate] = useState(plusDays(3));
  const [busy, setBusy] = useState(false);
  const dialTo = to || numbers[0]?.phone || "";

  const save = async () => {
    if (!outcome && !text.trim()) { toast.error("Pick an outcome or write a note"); return; }
    setBusy(true);
    try {
      const line = [`Call${dialTo ? ` to ${dialTo}` : ""}${outcome ? `: ${outcome}` : ""}`, text.trim()].filter(Boolean).join(" — ");
      await addAgeingNote(caseRow.case_id, { text: line, note_type: "call", author: "Ops", audience: "external" });
      if (promise) {
        if (!amount || !date) throw new Error("Promise needs an amount and a date");
        await createAgeingP2P(caseRow.case_id, { promised_amount: parseFloat(amount), promised_at: date, method: "bank_transfer", notes: outcome ? `Agreed on the call (${outcome})` : "Agreed on the call", created_by: "Ops" });
      }
      toast.success(promise ? "Call logged · promise scheduled" : "Call logged");
      onDone?.();
      onClose();
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="Call" testid="phone-call-sheet"
      footer={<Button className="w-full h-12 text-base" onClick={save} disabled={busy} data-testid="phone-call-save">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PhoneCall className="w-4 h-4 mr-2" />} Log this call</Button>}>
      {numbers.length > 1 && (
        <Select value={dialTo} onValueChange={setTo}>
          <SelectTrigger className="h-11 text-base" data-testid="phone-call-number"><SelectValue placeholder="Pick a number" /></SelectTrigger>
          <SelectContent>{numbers.map((n) => <SelectItem key={n.phone} value={n.phone}><span className="font-mono">{n.phone}</span> <span className="text-slate-500 ml-1">{n.label}</span></SelectItem>)}</SelectContent>
        </Select>
      )}
      {dialTo ? (
        <a href={`tel:${dialTo.replace(/[^+\d]/g, "")}`} className="flex items-center justify-center gap-2 h-14 rounded-xl bg-emerald-600 text-white text-lg font-semibold active:bg-emerald-700 sf-tap" data-testid="phone-call-dial">
          <Phone className="w-5 h-5" /> Call <span className="font-mono">{dialTo}</span>
        </a>
      ) : <div className="text-sm text-slate-500 rounded-lg border px-3 py-2">No phone number on this file — add a contact first, or log the call below.</div>}
      <p className="text-[11px] text-slate-500">Your phone dials; come back here afterwards to log what happened.</p>

      <SectionTitle>What happened</SectionTitle>
      <div className="flex gap-2 flex-wrap">
        {OUTCOMES.map((o) => (
          <button key={o} type="button" onClick={() => setOutcome(outcome === o ? "" : o)} className={cn("h-10 px-3 rounded-full border text-[13px] font-medium sf-tap", outcome === o ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700")} data-testid={`phone-call-outcome-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{o}</button>
        ))}
      </div>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Notes from the call (optional)" className="text-base" data-testid="phone-call-note" />
      <label className="flex items-center gap-3 h-11 text-sm sf-tap" data-testid="phone-call-promise-toggle">
        <input type="checkbox" checked={promise} onChange={(e) => setPromise(e.target.checked)} className="w-5 h-5 rounded border-slate-300" />
        <span>Customer promised to pay</span>
      </label>
      {promise && (
        <div className="grid grid-cols-2 gap-2">
          <div><div className="text-[11px] text-slate-500 mb-1">Amount {cur()}</div><Input type="number" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-11 text-base" data-testid="phone-call-p2p-amount" /></div>
          <div><div className="text-[11px] text-slate-500 mb-1">By</div><Input type="date" value={date} min={plusDays(0)} onChange={(e) => setDate(e.target.value)} className="h-11 text-base" data-testid="phone-call-p2p-date" /></div>
        </div>
      )}
    </BottomSheet>
  );
}

export function NoteSheet({ open, onClose, caseRow, onDone }) {
  const [text, setText] = useState("");
  const [external, setExternal] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!text.trim()) { toast.error("Write the note first"); return; }
    setBusy(true);
    try {
      await addAgeingNote(caseRow.case_id, { text: text.trim(), author: "Ops", audience: external ? "external" : null });
      toast.success(external ? "Note added · marked external" : "Note added");
      setText(""); onDone?.(); onClose();
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  return (
    <BottomSheet open={open} onClose={onClose} title="Add a note" testid="phone-note-sheet"
      footer={<Button className="w-full h-12 text-base" onClick={save} disabled={busy} data-testid="phone-note-save">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <StickyNote className="w-4 h-4 mr-2" />} Save note</Button>}>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} autoFocus placeholder="What happened? (e.g. Left VM, customer to call back at 3pm)" className="text-base" data-testid="phone-note-text" />
      <label className="flex items-center gap-3 h-11 text-sm sf-tap">
        <input type="checkbox" checked={external} onChange={(e) => setExternal(e.target.checked)} className="w-5 h-5 rounded border-slate-300" data-testid="phone-note-external" />
        <span className={external ? "font-medium text-emerald-700" : "text-slate-600"}>Mark as external (court-friendly)</span>
      </label>
    </BottomSheet>
  );
}

export function P2PSheet({ open, onClose, caseRow, onDone }) {
  const [amount, setAmount] = useState(caseRow.arrears_amount ? String(caseRow.arrears_amount) : "");
  const [date, setDate] = useState("");
  const [method, setMethod] = useState("bank_transfer");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const quick = [["Today", 0], ["+3 days", 3], ["+7 days", 7], ["+14 days", 14]];
  const save = async () => {
    if (!amount || !date) { toast.error("Amount and promise date required"); return; }
    setBusy(true);
    try {
      await createAgeingP2P(caseRow.case_id, { promised_amount: parseFloat(amount), promised_at: date, method, notes, created_by: "Ops" });
      toast.success("Promise to pay scheduled"); onDone?.(); onClose();
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  return (
    <BottomSheet open={open} onClose={onClose} title="Promise to pay" testid="phone-p2p-sheet"
      footer={<Button className="w-full h-12 text-base" onClick={save} disabled={busy} data-testid="phone-p2p-save">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Target className="w-4 h-4 mr-2" />} Schedule promise</Button>}>
      <div><div className="text-[11px] text-slate-500 mb-1">Amount {cur()}</div><Input type="number" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-12 text-lg" data-testid="phone-p2p-amount" /></div>
      <div>
        <div className="text-[11px] text-slate-500 mb-1">Promise date</div>
        <div className="flex gap-2 flex-wrap mb-2">
          {quick.map(([label, n]) => <button key={label} type="button" onClick={() => setDate(plusDays(n))} className={cn("h-9 px-3 rounded-full border text-[13px] font-medium sf-tap", date === plusDays(n) ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700")} data-testid={`phone-p2p-quick-${n}`}>{label}</button>)}
        </div>
        <Input type="date" value={date} min={plusDays(0)} onChange={(e) => setDate(e.target.value)} className="h-12 text-base" data-testid="phone-p2p-date" />
      </div>
      <div>
        <div className="text-[11px] text-slate-500 mb-1">Method</div>
        <Select value={method} onValueChange={setMethod}>
          <SelectTrigger className="h-12 text-base" data-testid="phone-p2p-method"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="bank_transfer">Bank transfer</SelectItem><SelectItem value="card">Card payment</SelectItem><SelectItem value="direct_debit">Direct debit</SelectItem><SelectItem value="cash">Cash</SelectItem><SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" className="h-12 text-base" data-testid="phone-p2p-notes" />
    </BottomSheet>
  );
}
